import { supabase } from '../lib/supabase';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

export const billingService = {
  /**
   * Get all payment methods for the current user
   */
  async getPaymentMethods() {
    try {
      const { data, error } = await supabase?.functions?.invoke('get-payment-methods', {
        method: 'POST',
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Add a new payment method using Stripe Elements
   * @param {string} paymentMethodId - Stripe payment method ID from Stripe Elements
   * @param {boolean} setAsDefault - Whether to set as default payment method
   */
  async addPaymentMethod(paymentMethodId, setAsDefault = true) {
    try {
      const { data, error } = await supabase?.functions?.invoke('update-payment-method', {
        body: { paymentMethodId, setAsDefault },
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Create Stripe payment method from card details
   * @param {object} cardElement - Stripe CardElement instance
   */
  async createPaymentMethod(cardElement) {
    try {
      const stripe = await stripePromise;
      if (!stripe) throw new Error('Stripe not loaded');

      const { paymentMethod, error } = await stripe?.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) throw error;
      return { data: paymentMethod, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Get billing information from user profile
   */
  async getBillingInfo(userId) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('billing_address, tax_id, business_name')
        ?.eq('id', userId)
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  /**
   * Update billing information
   * @param {object} billingData - Object containing billingAddress, taxId, businessName
   */
  async updateBillingInfo(billingData) {
    try {
      const { data, error } = await supabase?.functions?.invoke('update-billing-info', {
        body: billingData,
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },
};

export default billingService;